
import { useState } from 'react';
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface UserProfile {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
}

export const useFollowersData = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [followers, setFollowers] = useState<UserProfile[]>([]);
  const [following, setFollowing] = useState<UserProfile[]>([]);
  const [loadingFollowers, setLoadingFollowers] = useState(false);
  const [loadingFollowing, setLoadingFollowing] = useState(false);

  const loadFollowers = async () => {
    if (!user) return;
    
    try {
      setLoadingFollowers(true);
      
      const { data, error } = await supabase
        .from('user_follows')
        .select(`
          follower_id,
          profiles!follower_id(
            id,
            full_name,
            username,
            avatar_url
          )
        `)
        .eq('following_id', user.id);
      
      if (error) throw error;
      
      const mappedFollowers = (data || [])
        .filter(item => item.profiles)
        .map(item => {
          const profile = item.profiles as any;
          return {
            id: profile.id,
            full_name: profile.full_name,
            username: profile.username,
            avatar_url: profile.avatar_url
          };
        });
      
      setFollowers(mappedFollowers);
      
    } catch (error) {
      console.error("Error loading followers:", error);
      setFollowers([]);
    } finally {
      setLoadingFollowers(false);
    }
  };

  const loadFollowing = async () => {
    if (!user) return;
    
    try {
      setLoadingFollowing(true);
      
      const { data, error } = await supabase
        .from('user_follows')
        .select(`
          following_id,
          profiles!following_id(
            id,
            full_name,
            username,
            avatar_url
          )
        `)
        .eq('follower_id', user.id);
      
      if (error) throw error;
      
      const mappedFollowing = (data || [])
        .filter(item => item.profiles)
        .map(item => {
          const profile = item.profiles as any;
          return {
            id: profile.id,
            full_name: profile.full_name,
            username: profile.username,
            avatar_url: profile.avatar_url
          };
        });
      
      setFollowing(mappedFollowing);
      
    } catch (error) {
      console.error("Error loading following:", error);
      setFollowing([]);
    } finally {
      setLoadingFollowing(false);
    }
  };

  return {
    followers,
    following,
    loadingFollowers,
    loadingFollowing,
    loadFollowers,
    loadFollowing
  };
};
